import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, shouldRenderMobile, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsTextHtmlAPI } from '@philips-emr/hpms-provider-accreditation-components';
import { ActionAPI, DeckAPI, FetchAPI, FormAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, SessionStorageService } from '@philips-emr/tws-core/lib/services';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitAppointmentsAliases as Aliases } from '../../codegen/constants/aliases/technical-visit-appointments-aliases';
import BaseController from '../BaseController';

import './technical-visit.css';

interface Appointment {
  ieStatus: number,
  dsStatus: string,
  nrSeqAppointment: number,
  history: string,
  message: string,
  suggestedDate: string,
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_APPOINTMENTS })
export default class TechnicalVisitAppointmentsController extends BaseController {

  @Inject(Aliases.FETCH_APPOINTMENTS)
  private fetchAppointments: FetchAPI;

  @Inject(Aliases.HPMS_TEXT_HTML)
  private hpmsText: HpmsTextHtmlAPI;

  @Inject(Aliases.ACTION_REFUSE)
  private actionRefuse: ActionAPI;

  @Inject(Aliases.ACTION_CONFIRM)
  private actionConfirm: ActionAPI;

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;

  @Inject(Aliases.FORM_DATA)
  private formData: FormAPI;

  @Inject(Aliases.DECK_HISTORIES)
  private deck: DeckAPI;

  @Inject(Aliases.FETCH_HISTORIES)
  private fetchHistories: FetchAPI;

  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;

  @OnMount([Aliases.FETCH_HISTORIES, Aliases.FETCH_MENUS_MOBILE])
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<{ nrSeqAppointment: string }>): void {
    const nrSeqAppointment = event.value.nrSeqAppointment;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
  }

  @OnDynamicContentRender(Aliases.CARD_HISTORIES)
  onRenderAppointmentsHistory(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus } = data as unknown as Appointment;
    const stampForm = this.getStampAppointments(ieStatus);
    if (stampForm) {
      render((): ReactElement => stampForm);
    }
  }

  @OnAfterFetch([Aliases.FETCH_HISTORIES, Aliases.FETCH_MENUS_MOBILE])
  onAfterFetchHistories(event: FetchEvent): void {
    const data = event.data as Appointment[];
    if (!data) return;
    let nrSeqAppointment = SessionStorageService.getItem('nrSeqAppointment');
    SessionStorageService.removeItem('nrSeqAppointment');
    const { nrSeqAppointment: nrSequence } = data[0];
    if (!nrSeqAppointment) {
      nrSeqAppointment = nrSequence;
    }
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
    this.deck.selectCard(String(nrSeqAppointment));
  }

  @OnSelectItem(Aliases.DECK_HISTORIES)
  onClickAppointment(event: SelectItemEvent<any>): void {
    const { nrSeqAppointment } = event.item as Appointment;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.fetchAppointments.setParameters({ nrSeqVisit, nrSeqAppointment });
    this.fetchAppointments.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_APPOINTMENTS)
  onMountTermoTexto(event: FetchEvent): void {
    const { history, ieStatus, nrSeqAppointment, suggestedDate } = event.data as Appointment;
    this.actionRefuse.setEnabled(ieStatus == 1);
    this.actionConfirm.setEnabled(ieStatus == 1);
    this.hpmsText.setData(history);
    this.formMenuMobile.setData({ menuMobile: { suggestedDate, nrSeqAppointment } });
  }

  @OnSubmit(Aliases.FORM_MESSAGE)
  onSubmitReject(event: SubmitEvent): void {
    const nrSeqAppointment = this.context.get('nrSeqAppointment');
    const { message } = event.record as Appointment;
    HttpService.post(Endpoint.POST.REJECT_APPOINTMENT, { nrSeqAppointment, message }).then(() => {
      this.actionRefuse.setEnabled(false);
      this.actionConfirm.setEnabled(false);
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.reject-appointment-success-message'));
      this.panelModal.setVisible(false);
    }).catch(() => ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request')));
  }

  @OnClick(Aliases.ACTION_CONFIRM)
  onClickConfirm(): void {
    const { nrSeqAppointment } = this.formData.getData() as unknown as Appointment;
    this.confirmAppointment(nrSeqAppointment);
  }

  @OnClick(Aliases.ACTION_REFUSE)
  onClickRefuse(): void {
    const { nrSeqAppointment } = this.formData.getData() as unknown as Appointment;
    this.refuseAppointment(nrSeqAppointment);
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_MESSAGE)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.PANEL_MOBILE)
  onMountPanelMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  private confirmAppointment(nrSeqAppointment: number): void {
    HttpService.post(Endpoint.POST.CONFIRM_APPOINTMENT, { nrSeqAppointment }).then(() => {
      catchRenderException(() => {
        this.actionRefuse.setEnabled(false);
        this.actionConfirm.setEnabled(false);
        this.fetchHistories.forceFetch();
      });
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.confirm-appointment-sucess-message'));
    }).catch(() => ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request')));
  }

  private refuseAppointment(nrSeqAppointment: number): void {
    this.panelModal.setVisible(true);
    this.context.set({ nrSeqAppointment });
  }

  private getStampAppointments(ieStatus: number): ReactElement | null {
    let stampProps = null;
    switch (ieStatus) {
      case 1:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label: '' };
        break;
      case 2:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label: '' };
        break;
      case 3:
        stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label: '' };
        break;
      case 4:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label: '' };
        break;
      case 5:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label: '' };
        break;
      case 6:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label: '' };
        break;
      default:
        return null;
    }
    return <Stamp {...stampProps} />;
  }
}
